import type { FlowState } from '~/flow/types';
import type { FeatureFlags } from '~/helpers/typescript';
import { transformLegacyRepresentationToJson, keyServicesObjectByInternalName } from '../../engine/transformation';
import { transformServices } from '../../engine/transformation/workflowProps';
import type { WorkflowPropsResponse, WorkflowFailureHandlingMode, WorkflowProps } from '../../engine/types';
import { initialState } from '../state';
import type { SdkConfigPayload } from 'flow/editor/components/MenuBar/MenuBarModals/ThreeDotsModals/SDKConfigModal/types';
import { indexSnippetsMetadataByToken } from 'flow/service/snippets';
import { formatUniversalAttributes, indexServiceAttributesById } from '../../../service/services/attributes';

function createWorkflowInfo(workflowData: FlowState, workflowProps: WorkflowPropsResponse) {
  return workflowData.version?.application_token
    ? {
      name: workflowData.version.application_name,
      token: workflowData.version.application_token,
      publicVersion: workflowData.version.public_version,
    }
    : {
      name: workflowProps.workflow.name,
      token: workflowProps.workflow.application_token,
      publicVersion: null,
    };
}

// TODO(BUILD-1869): transform props
function createInitialWorkflowProps(
  workflowProps: WorkflowPropsResponse,
  publishedAttributes: WorkflowProps['publishedAttributes'],
): WorkflowProps {
  return {
    aggregationConfig: workflowProps.aggregationConfig,
    allWorkflows: workflowProps.allWorkflows,
    categories: workflowProps.categories,
    customLists: workflowProps.customLists,
    customers: workflowProps.customers,
    globalServiceConfigOptions: workflowProps.globalServiceConfigOptions,
    importableAttributes: workflowProps.importableAttributes,
    lists: workflowProps.lists,
    outcomes: workflowProps.outcomes,
    publishedAttributes,
    reasonCodeGroups: workflowProps.reasonCodeGroups,
    reasonCodes: workflowProps.reasonCodes,
    recurringWorkflows: workflowProps.recurringWorkflows,
    sandboxPersonas: workflowProps.sandboxPersonas,
    // todo BUILD-2731 - remove the outer transformation call
    services: transformServices(
      keyServicesObjectByInternalName(workflowProps.services),
    ),
    snippets: indexSnippetsMetadataByToken(workflowProps.snippets),
    tagTypes: workflowProps.tagTypes,
    tags: workflowProps.tags,
    universalAttributes: indexServiceAttributesById(
      formatUniversalAttributes(workflowProps.universalAttributes),
    ),
    workflow: workflowProps.workflow,
  };
}

function withEphemeralPublishedAttributes(
  publishedAttributes: WorkflowPropsResponse['publishedAttributes'],
): WorkflowProps['publishedAttributes'] {
  return publishedAttributes.map(pa => ({ ...pa, isEphemeral: false }));
}

export function transformWorkflowPropsResponse(
  workflowProps: WorkflowPropsResponse,
): WorkflowProps {
  return createInitialWorkflowProps(
    workflowProps,
    withEphemeralPublishedAttributes(workflowProps.publishedAttributes),
  );
}

function createInitialWorkflowData(
  transformationResult: any,
  sdkConfig: SdkConfigPayload,
  workflowInfo: ReturnType<typeof createWorkflowInfo>,
  workflowData: FlowState,
) {
  return {
    ...transformationResult,
    ...workflowInfo,
    sdkConfig,
    workflowsOutcomeReasonTagsByTagId: workflowData.workflowsOutcomeReasonTagsByTagId,
    failureHandlingMode: (workflowData.version.use_best_effort_threshold_rule_parser
      ? 'best-effort'
      : 'full-threshold') as WorkflowFailureHandlingMode,
  };
}

function createFeatureFlagsForStore(featureFlags: Partial<FeatureFlags>) {
  return {
    allowBestEffortRuleEvaluations: featureFlags.allowBestEffortRuleEvaluations,
    customTransactionAttributes: featureFlags.customTransactionAttributes,
    dynamicDataRouting: featureFlags.dynamicDataRouting,
    enableWorkflowDiff: featureFlags.enableWorkflowDiff,
    useTyphonWorkflowEndpoints: featureFlags.useTyphonWorkflowEndpoints,
    workflowEditorUndoRedo: featureFlags.workflowEditorUndoRedo,
  };
}

export function transformWorkflowInitializationData({
  workflowData,
  workflowProps,
  featureFlags,
}: {
  workflowData: FlowState;
  workflowProps: WorkflowPropsResponse;
  featureFlags: Partial<FeatureFlags>;
}) {
  // Transform published attributes once, before passing to both transformation and props
  const publishedAttributesWithEphemeral = withEphemeralPublishedAttributes(
    workflowProps.publishedAttributes,
  );

  const workflowInfo = createWorkflowInfo(workflowData, workflowProps);
  const initWorkflowProps = createInitialWorkflowProps(
    workflowProps,
    publishedAttributesWithEphemeral,
  );

  // Transform legacy representation data as part of initialization
  const transformationResult = transformLegacyRepresentationToJson({
    actions: workflowData.actions,
    actionOptions: workflowData.actionOptions,
    thresholds: workflowData.thresholds,
    dataRoutes: workflowData.dataRoutes,
    tags: workflowProps.tags,
    services: workflowProps.services,
    outcomes: workflowProps.outcomes,
    publishedAttributes: publishedAttributesWithEphemeral,
    matrixModels: workflowProps.matrixModels,
    lists: workflowProps.lists,
    customLists: workflowProps.customLists,
    reasonCodeGroups: workflowProps.reasonCodeGroups,
    snippets: initWorkflowProps.snippets, // indexed snippets by token,
    universalAttributes: initWorkflowProps.universalAttributes,
  });

  const { legacyMappings, ...restTransformationResult } = transformationResult;
  const initWorkflowData = createInitialWorkflowData(
    restTransformationResult,
    workflowData.sdkConfig,
    workflowInfo,
    workflowData,
  );

  const featureFlagsForStore = createFeatureFlagsForStore(featureFlags);

  return {
    initWorkflowProps,
    initWorkflowData,
    legacyMappings,
    initWorkflowFeatures: initialState.workflowFeatures,
    featureFlagsForStore,
  };
}
